// Piano visualizer (§5.5): keyboard auto-zoomed to the range in use (whole
// octaves, at least two), active keys filled with the accent, context notes as
// subtle dots, a middle-C marker, optional note labels, click-to-enter.

import { CanvasView, dot, fitText, roundRect } from './surface';
import {
  DEFAULT_PIANO_RANGE,
  MIDDLE_C,
  type PianoKey,
  type PianoLayout,
  hitTestPiano,
  normalizePianoRange,
  pianoLayout,
} from './piano-geometry';
import { resolveTheme, sameTheme, type VizTheme } from './theme';
import { DEFAULT_KEY, copyNotes, labelOf, sameNotes, type VizNote, type VizOptionsBase } from './types';

export interface PianoOptions extends VizOptionsBase {
  /** Label C keys with their octave (C3, C4 …). Default true. */
  octaveLabels?: boolean;
}

const DEFAULT_OPTIONS: PianoOptions = { noteNames: 'abc', key: DEFAULT_KEY, touchInput: false, octaveLabels: true };

export class PianoView extends CanvasView<number> {
  private baseRange: [number, number] = [DEFAULT_PIANO_RANGE[0], DEFAULT_PIANO_RANGE[1]];
  private range: [number, number] = [DEFAULT_PIANO_RANGE[0], DEFAULT_PIANO_RANGE[1]];
  private opts: PianoOptions = { ...DEFAULT_OPTIONS };
  private theme: VizTheme;
  private active: VizNote[] = [];
  private context: VizNote[] = [];
  private activeSet = new Map<number, string>();
  private contextSet = new Map<number, string>();
  private layout: PianoLayout | null = null;
  private pickCb: ((midi: number) => void) | null = null;

  constructor(canvas: HTMLCanvasElement) {
    super(canvas);
    this.theme = resolveTheme(canvas);
    this.resize();
  }

  /** Range in use (e.g. the instrument's); expanded to whole octaves, ≥ 2 octaves. */
  setRange(lowMidi: number, highMidi: number): void {
    const r: [number, number] = [Math.round(Math.min(lowMidi, highMidi)), Math.round(Math.max(lowMidi, highMidi))];
    if (r[0] === this.baseRange[0] && r[1] === this.baseRange[1]) return;
    this.baseRange = r;
    this.updateRange();
  }

  /** Cheap when unchanged (safe to call every frame). */
  setNotes(active: readonly VizNote[], context: readonly VizNote[]): void {
    if (sameNotes(active, this.active) && sameNotes(context, this.context)) return;
    this.active = copyNotes(active);
    this.context = copyNotes(context);
    this.relabel();
    this.updateRange();
    this.invalidate();
  }

  setOptions(o: Partial<PianoOptions>): void {
    const prev = this.opts;
    const next: PianoOptions = { ...prev, ...o, key: o.key ?? prev.key };
    this.opts = next;
    this.setInteractive(next.touchInput);
    const themeChanged = !sameTheme(prev.theme, next.theme);
    const labelsChanged =
      prev.noteNames !== next.noteNames || prev.key.fifths !== next.key.fifths || prev.key.mode !== next.key.mode || prev.octaveLabels !== next.octaveLabels;
    if (!themeChanged && !labelsChanged) return;
    if (themeChanged) this.theme = resolveTheme(this.canvas, next.theme);
    if (labelsChanged) this.relabel();
    this.invalidate();
  }

  /** Touch-input callback. Returns an unsubscribe function. */
  onPick(cb: ((midi: number) => void) | null): () => void {
    this.pickCb = cb;
    return () => {
      if (this.pickCb === cb) this.pickCb = null;
    };
  }

  /** Displayed range after normalization. */
  getRange(): [number, number] {
    return [this.range[0], this.range[1]];
  }

  override destroy(): void {
    super.destroy();
    this.pickCb = null;
  }

  // ----------------------------------------------------------------------

  private relabel(): void {
    const { noteNames, key } = this.opts;
    this.activeSet = new Map(this.active.map((n) => [Math.round(n.midi), labelOf(n, noteNames, key)]));
    this.contextSet = new Map(this.context.filter((n) => !this.activeSet.has(Math.round(n.midi))).map((n) => [Math.round(n.midi), labelOf(n, noteNames, key)]));
  }

  private updateRange(): void {
    let lo = this.baseRange[0];
    let hi = this.baseRange[1];
    // Never hide a note: grow to include everything shown.
    for (const m of this.activeSet.keys()) {
      lo = Math.min(lo, m);
      hi = Math.max(hi, m);
    }
    for (const m of this.contextSet.keys()) {
      lo = Math.min(lo, m);
      hi = Math.max(hi, m);
    }
    const r = normalizePianoRange(lo, hi);
    if (r[0] === this.range[0] && r[1] === this.range[1]) return;
    this.range = r;
    this.layout = null;
    this.invalidate();
  }

  protected override onResize(): void {
    this.layout = null;
  }

  private getLayout(): PianoLayout {
    this.layout ??= pianoLayout(this.range[0], this.range[1], this.width, this.height, 0);
    return this.layout;
  }

  protected hitTest(x: number, y: number): number | null {
    if (this.width <= 0) return null;
    return hitTestPiano(this.getLayout(), x, y);
  }

  protected sameHit(a: number, b: number): boolean {
    return a === b;
  }

  protected activate(midi: number): void {
    this.pickCb?.(midi);
  }

  protected paint(c: CanvasRenderingContext2D, w: number, h: number): void {
    const l = this.getLayout();
    const t = this.theme;
    c.clearRect(0, 0, w, h);
    c.fillStyle = t.background;
    c.fillRect(0, 0, w, h);
    c.textAlign = 'center';
    c.textBaseline = 'alphabetic';
    const gap = l.whiteW >= 8 ? 1 : 0.5;
    const radius = Math.min(4, l.whiteW * 0.15);

    // White keys.
    for (const k of l.whites) {
      const on = this.activeSet.has(k.midi);
      const hover = this.interactive && this.hover === k.midi;
      c.fillStyle = on ? t.active : t.whiteKey;
      roundRect(c, k.x + gap / 2, k.y - radius, k.w - gap, k.h + radius - gap, radius);
      c.fill();
      if (hover && !on) this.hoverFill(c, k);
    }
    // Context dots and labels on white keys.
    for (const k of l.whites) this.decorate(c, l, k);

    // Black keys.
    for (const k of l.blacks) {
      const on = this.activeSet.has(k.midi);
      c.fillStyle = on ? t.active : t.blackKey;
      roundRect(c, k.x, k.y - radius, k.w, k.h + radius, Math.min(3, radius));
      c.fill();
      if (!on) {
        // A little bevel so black keys read on dark backgrounds.
        c.fillStyle = 'rgba(255,255,255,0.08)';
        c.fillRect(k.x + k.w * 0.15, k.h - Math.max(3, k.h * 0.08), k.w * 0.7, Math.max(2, k.h * 0.05));
      }
      if (this.interactive && this.hover === k.midi && !on) this.hoverFill(c, k);
      this.decorate(c, l, k);
    }
    // Top shadow line (key bed).
    c.fillStyle = 'rgba(0,0,0,0.35)';
    c.fillRect(0, 0, w, 2);
  }

  private hoverFill(c: CanvasRenderingContext2D, k: PianoKey): void {
    c.globalAlpha = 0.35;
    c.fillStyle = this.theme.accent;
    c.fillRect(k.x + 1, k.y, k.w - 2, k.h - 1);
    c.globalAlpha = 1;
  }

  /** Context dot, labels and the middle-C marker for one key. */
  private decorate(c: CanvasRenderingContext2D, l: PianoLayout, k: PianoKey): void {
    const t = this.theme;
    const cx = k.x + k.w / 2;
    const activeLabel = this.activeSet.get(k.midi);
    const ctxLabel = this.contextSet.get(k.midi);
    const r = Math.max(2.5, Math.min(6, k.w * 0.28));
    const bottom = k.y + k.h;
    const dotY = bottom - (k.black ? r + 4 : r + 16);
    const labelY = bottom - 5;
    if (activeLabel !== undefined) {
      if (activeLabel && k.w >= 9) {
        c.fillStyle = t.activeText;
        fitText(c, activeLabel, cx, k.black ? bottom - 6 : labelY, k.w - 2);
      }
      return;
    }
    if (ctxLabel !== undefined) {
      dot(c, cx, dotY, r, k.black ? t.fret : t.context);
      if (ctxLabel && k.w >= 9 && !k.black) {
        c.fillStyle = t.context;
        fitText(c, ctxLabel, cx, labelY, k.w - 2);
      }
      return;
    }
    if (k.midi === MIDDLE_C) dot(c, cx, bottom - 20, Math.max(1.5, Math.min(3, k.w * 0.12)), t.fret);
    if (!k.black && this.opts.octaveLabels !== false && k.midi % 12 === 0 && l.whiteW >= 14) {
      c.fillStyle = t.fret;
      fitText(c, `C${k.midi / 12 - 1}`, cx, labelY, k.w - 2, 600, 9);
    }
  }
}
